import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Upload, Type, FileText, Loader2, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const SAMPLES = [
  "Quantum Physics & Superposition",
  "Neuroscience & Synaptic Plasticity",
  "Organic Chemistry Mechanisms",
  "Machine Learning & Neural Networks",
];

export default function StartScreen({ onStartTopic, onStartNotes, loading }) {
  const [topic, setTopic] = useState("");
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const submitTopic = (t) => {
    const value = (t ?? topic).trim();
    if (!value || loading) return;
    onStartTopic(value);
  };

  const handleFile = (file) => {
    if (!file || loading) return;
    onStartNotes(file);
  };

  return (
    <div className="app-bg min-h-screen flex flex-col items-center justify-center px-6 py-16" data-testid="start-screen">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-2xl text-center"
      >
        <span className="font-mono-fx text-xs uppercase tracking-[0.3em] text-purple-400">Eureka FractalNotes AI</span>
        <h1 className="mt-4 font-display text-4xl sm:text-6xl font-extrabold tracking-tight text-white glow-text leading-[1.02]">
          Map any subject into a<br />living 3×3 grid.
        </h1>
        <p className="mt-5 text-base text-purple-200/75 max-w-lg mx-auto">
          Type a topic or drop your class notes. The AI extracts the 8 key concepts and lets you drill down forever.
        </p>

        <Tabs defaultValue="type" className="mt-10">
          <TabsList className="bg-surface-card/60 border border-purple-500/20 rounded-full p-1 mx-auto">
            <TabsTrigger data-testid="tab-type" value="type" className="rounded-full data-[state=active]:bg-purple-600 data-[state=active]:text-white text-purple-200 px-5">
              <Type className="w-4 h-4 mr-2" /> Type a subject
            </TabsTrigger>
            <TabsTrigger data-testid="tab-upload" value="upload" className="rounded-full data-[state=active]:bg-purple-600 data-[state=active]:text-white text-purple-200 px-5">
              <Upload className="w-4 h-4 mr-2" /> Upload notes
            </TabsTrigger>
          </TabsList>

          <TabsContent value="type" className="mt-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                data-testid="start-subject-input"
                autoFocus
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitTopic()}
                placeholder="e.g. The French Revolution"
                disabled={loading}
                style={{ color: "#F5F3FF", WebkitTextFillColor: "#F5F3FF" }}
                className="h-14 bg-[#0D0B18] border-purple-500/25 !text-white caret-purple-400 placeholder:text-purple-300/40 text-base rounded-xl focus-visible:ring-purple-500"
              />
              <Button
                data-testid="start-explore-button"
                onClick={() => submitTopic()}
                disabled={loading || !topic.trim()}
                className="h-14 px-7 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-semibold shadow-[0_0_24px_rgba(168,85,247,0.45)]"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Explore <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
            </div>

            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              {SAMPLES.map((s) => (
                <button
                  key={s}
                  data-testid={`sample-${s.slice(0, 6).toLowerCase().replace(/\s/g, "")}`}
                  onClick={() => { setTopic(s); submitTopic(s); }}
                  disabled={loading}
                  className="text-xs px-3 py-1.5 rounded-full bg-purple-500/8 border border-purple-500/20 text-purple-200/80 hover:bg-purple-500/20 hover:text-white transition-colors flex items-center gap-1.5"
                >
                  <Sparkles className="w-3 h-3" /> {s}
                </button>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="upload" className="mt-6">
            <div
              data-testid="start-upload-dropzone"
              onClick={() => !loading && fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
              className={
                "cursor-pointer rounded-2xl border-2 border-dashed p-10 transition-all grid-noise relative overflow-hidden " +
                (dragging ? "border-purple-400 bg-purple-500/10" : "border-purple-500/25 bg-surface-card/40 hover:border-purple-400/50")
              }
            >
              <input
                ref={fileRef}
                type="file"
                data-testid="start-file-input"
                accept=".pdf,.docx,.txt,.md,.png,.jpg,.jpeg,.webp"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              {loading ? (
                <div className="flex flex-col items-center gap-3 text-purple-200">
                  <Loader2 className="w-8 h-8 animate-spin" />
                  <p className="text-sm">Reading your notes with AI…</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <div className="p-3 rounded-2xl bg-purple-500/15 border border-purple-500/25">
                    <FileText className="w-7 h-7 text-purple-300" />
                  </div>
                  <p className="text-white font-semibold">Drop your lecture notes here</p>
                  <p className="text-xs text-purple-300/60">PDF, DOCX, TXT, MD, or an image (JPG / PNG / WEBP)</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </motion.div>
    </div>
  );
}
